import React, { useState } from "react";

// --- 1. Policy Data ---
// All the text content for the pop-ups is stored here.
const policyData = {
  faq: {
    title: "Frequently Asked Questions",
    content: (
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold">How do I place a custom order?</h4>
          <p>You can contact us via WhatsApp or email with your requirements, and our team will guide you through the customization process.</p>
        </div>
        <div>
          <h4 className="font-semibold">What is the estimated delivery time?</h4>
          <p>Delivery usually takes 7-10 business days for domestic orders. Custom orders may take longer, which will be communicated during the consultation.</p>
        </div>
        <div>
          <h4 className="font-semibold">Do you ship internationally?</h4>
          <p>Currently, we only ship within India. We are working on expanding our services to international customers soon.</p>
        </div>
      </div>
    ),
  },
  shipping: {
    title: "Shipping Policy",
    content: (
      <p>We offer shipping across India. All orders are processed within 2-3 business days. You will receive a shipment confirmation email once your order has shipped containing your tracking number(s). Shipping charges for your order will be calculated and displayed at checkout.</p>
    ),
  },
  returns: {
    title: "Returns & Exchanges",
    content: (
      <p>We accept returns within 7 days of delivery for a store credit or exchange. Items must be in their original, unworn condition with all tags attached. Please note that custom-made outfits are not eligible for returns. To initiate a return, please contact our support team.</p>
    ),
  },
  cancellation: {
    title: "Cancellation Policy",
    content: (
       <p>You can cancel your order within 24 hours of placing it for a full refund. For custom orders, cancellations are only possible before the production process begins. Please contact our support team immediately for any cancellation requests.</p>
    ),
  },
  'privacy-policy': {
    title: "Privacy Policy",
    content: (
       <p>Your privacy is important to us. We do not share your personal information with third parties except to comply with the law, develop our products, or protect our rights. We collect information like your name, shipping address, and email to process your orders and keep you updated.</p>
    ),
  },
  'terms-of-service': {
    title: "Terms of Service",
    content: (
       <p>By accessing or using our service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the service. All content on this site, including images and text, is the property of Monvi Styles and protected by copyright laws.</p>
    ),
  },
};


// --- 2. Modal Component ---
// This is the pop-up component, defined locally within this file.
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) {
    return null;
  }

  return (
    // Backdrop
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center"
      onClick={onClose}
    >
      {/* Modal Content */}
      <div 
        className="bg-white rounded-lg shadow-xl w-11/12 md:w-2/3 lg:w-1/2 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-800 text-2xl"
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>
        {/* Modal Body */}
        <div className="p-6 overflow-y-auto text-gray-700">
          {children}
        </div>
        {/* Modal Footer */}
        <div className="flex justify-end p-4 border-t">
          <button 
            onClick={onClose} 
            className="bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// --- 3. Main Footer Component ---
const Footer = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', content: null });

  const openModal = (policyKey) => {
    if (policyData[policyKey]) {
      setModalContent(policyData[policyKey]);
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Column 1: Company Info */}
            <div className="lg:col-span-1">
               <div className="flex items-center mb-4">
                <img
                  src="/images/logos/Monvi_Styles_Brand_Identity_Design-removebg-preview (1).png"
                  alt="Monvi Styles"
                  className="h-12 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <h3 className="text-xl font-bold ml-2" style={{ display: 'none' }}>MONVI</h3>
              </div>
              <p className="text-gray-300 mb-4">
                Our mission is to make every woman feel confident and beautiful with our customized outfits. We use the finest materials, emphasizing quality craftsmanship and attention to detail.
              </p>
              <div className="flex space-x-4">
                 {/* Social Media Icons... */}
                 <a href="https://www.instagram.com/monvistyles?igsh=YmJjbnJxOGE4bjVw" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram"><svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path fillRule="evenodd" d="M12 2C8.74 2 8.279.014 6.924.06c-1.355.045-2.288.228-3.104.538a4.915 4.915 0 00-1.77 1.153 4.915 4.915 0 00-1.153 1.77c-.31.816-.493 1.75-.538 3.104C.014 8.28 0 8.74 0 12s.014 3.72.06 5.076c.045 1.355.228 2.288.538 3.104a4.915 4.915 0 001.153 1.77 4.915 4.915 0 001.77 1.153c.816.31 1.75.493 3.104.538C8.28 23.986 8.74 24 12 24s3.72-.014 5.076-.06c1.355-.045 2.288-.228 3.104-.538a4.915 4.915 0 001.77-1.153 4.915 4.915 0 001.153-1.77c.31-.816.493-1.75.538-3.104C23.986 15.72 24 15.26 24 12s-.014-3.72-.06-5.076c-.045-1.355-.228-2.288-.538-3.104a4.915 4.915 0 00-1.153-1.77A4.915 4.915 0 0018.976.6c-.816-.31-1.75-.493-3.104-.538C15.72.014 15.26 0 12 0zm0 4.865A7.135 7.135 0 114.865 12 7.135 7.135 0 0112 4.865zm0 11.47A4.335 4.335 0 1012 7.665a4.335 4.335 0 000 8.67z" clipRule="evenodd" /><path d="M18.89 6.832a1.44 1.44 0 100-2.88 1.44 1.44 0 000 2.88z" /></svg></a>
                 <a href="https://wa.me/919676111210" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="WhatsApp"><svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.42 1.29 4.89L2 22l5.25-1.38c1.41.78 3.02 1.21 4.75 1.21h.01c5.46 0 9.91-4.45 9.91-9.91s-4.45-9.91-9.91-9.91zM18.1 16.4c-.2-.1-.7-.35-1.02-.45-.32-.1-.55-.15-.78.15-.23.3-.39.45-.48.55-.09.1-.18.1-.33.05-.15-.05-1.02-.38-1.94-1.2-.72-.63-1.2-1.41-1.35-1.66-.15-.25-.03-.38.12-.51.13-.11.28-.28.42-.42.14-.14.18-.23.28-.38.09-.15.05-.28-.02-.43s-.78-1.88-1.07-2.58c-.28-.68-.56-.59-.78-.59-.21 0-.45-.05-.68-.05-.23 0-.6.09-.92.44-.32.35-1.22 1.19-1.22 2.89s1.25 3.35 1.43 3.58c.17.23 2.44 3.74 5.91 5.21.82.37 1.47.59 1.97.75.78.25 1.48.21 2.05.13.62-.09 1.88-.77 2.15-1.5.28-.73.28-1.35.2-1.45-.09-.1-.28-.15-.48-.25z" /></svg></a>
                 <a href="#" className="text-gray-400 hover:text-white transition-colors" aria-label="Pinterest"><svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/></svg></a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="/" className="text-gray-300 hover:text-white transition-colors">Home</a></li>
                <li><a href="/collections/dresses" className="text-gray-300 hover:text-white transition-colors">Dresses</a></li>
                {/* ... other quick links */}
              </ul>
            </div>

            {/* Column 3: Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Information</h3>
              <ul className="space-y-2">
                <li><button onClick={() => openModal('faq')} className="text-left text-gray-300 hover:text-white transition-colors">FAQ</button></li>
                <li><button onClick={() => openModal('shipping')} className="text-left text-gray-300 hover:text-white transition-colors">Shipping Policy</button></li>
                <li><button onClick={() => openModal('returns')} className="text-left text-gray-300 hover:text-white transition-colors">Returns & Exchanges</button></li>
                <li><button onClick={() => openModal('cancellation')} className="text-left text-gray-300 hover:text-white transition-colors">Cancellation Policy</button></li>
              </ul>
            </div>

            {/* Column 4: Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
              {/* ... contact details ... */}
               <div className="space-y-4 text-gray-300"><div className="flex items-start"><svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg><span>Monvistyles</span></div><div className="flex items-center"><svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg><a href="tel:+919676111210" className="hover:text-white transition-colors">+91 9676111210</a></div><div className="flex items-center"><svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg><a href="mailto:monvistyles@gmail.com" className="hover:text-white transition-colors">monvistyles@gmail.com</a></div><div className="text-sm pt-2"><p className="font-semibold">Support Hours:</p><p>Mon - Sat, 10 AM - 5 PM (IST)</p></div></div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Monvi Styles. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
                 <button onClick={() => openModal('privacy-policy')} className="text-gray-400 hover:text-white transition-colors">
                    Privacy Policy
                 </button>
                 <button onClick={() => openModal('terms-of-service')} className="text-gray-400 hover:text-white transition-colors">
                    Terms of Service
                 </button>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* The Modal is rendered here but is only visible when isModalOpen is true */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        title={modalContent.title}
      >
        {modalContent.content}
      </Modal>
    </>
  );
};

export default Footer;